import React, {
  useRef,
  useEffect,
  useImperativeHandle,
  forwardRef,
  useState,
  memo,
} from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { environment } from '../../../environments/environment';

const SCRIPT_URL = environment.squareScriptUrl;

// Module-level flag to prevent multiple instances from initializing simultaneously
let globalInitializationInProgress = false;

const SquareGiftCard = forwardRef(function SquareGiftCard(
  {
    applicationId = environment.squareApplicationId,
    // Prefer `currentStoreId` when provided by parent components. Keep
    // `locationId` for backward compatibility.
    currentStoreId = undefined,
    locationId = environment.squareLocationId,
    onToken,
    onError,
  },
  ref
) {
  // Determine the effective location id to use for Square Payments.
  const effectiveLocationId = currentStoreId || locationId;
  // Generate unique ID for this instance to prevent conflicts
  const containerIdRef = useRef(`sq-gift-card-container-${Math.random().toString(36).substr(2, 9)}`);
  const giftCardRef = useRef(null);
  const paymentsRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [initError, setInitError] = useState(null);
  const renderCountRef = useRef(0);
  const isInitializedRef = useRef(false);
  const prevPropsRef = useRef({});

  // Track renders
  renderCountRef.current += 1;

  const currentProps = {
    applicationId,
    currentStoreId,
    locationId,
    effectiveLocationId,
    ready,
    isInitialized: isInitializedRef.current,
    onToken: typeof onToken,
    onError: typeof onError,
  };

  // Check what changed since last render
  if (renderCountRef.current > 1) {
    const changes = {};
    Object.keys(currentProps).forEach((key) => {
      if (prevPropsRef.current[key] !== currentProps[key]) {
        changes[key] = {
          from: prevPropsRef.current[key],
          to: currentProps[key],
        };
      }
    });
  }

  prevPropsRef.current = currentProps;

  // Count visible gift card inputs for user debugging
  useEffect(() => {
    const checkVisibleInputs = () => {
      const containers = document.querySelectorAll(
        '[id^="sq-gift-card-container"]'
      );
      const visibleContainers = Array.from(containers).filter((el) => {
        const rect = el.getBoundingClientRect();
        return rect.width > 0 && rect.height > 0;
      });

      // Check for duplicate inputs (silently handled)
      if (visibleContainers.length > 1) {
        // Duplicate inputs detected - handled silently
      }
    };

    // Check after a short delay to allow DOM to settle
    const timer = setTimeout(checkVisibleInputs, 100);
    return () => clearTimeout(timer);
  }, [ready]);

  useEffect(() => {
    let mounted = true;

    const loadScript = async () => {
      try {
        // Prevent double initialization - check BEFORE any async operations
        if (isInitializedRef.current || globalInitializationInProgress) {
          return;
        }

        // Set flags immediately to prevent race conditions
        isInitializedRef.current = true;
        globalInitializationInProgress = true;

        if (!effectiveLocationId) {
          isInitializedRef.current = false; // Reset on error
          globalInitializationInProgress = false;
          throw new Error(
            'No location ID provided for Gift Card initialization'
          );
        }

        // Load Square SDK if not already loaded
        if (!window.Square) {
          await new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = SCRIPT_URL;
            script.onload = resolve;
            script.onerror = reject;
            document.head.appendChild(script);
          });
        }

        if (!mounted) {
          return;
        }

        // Initialize Square Payments
        const payments = window.Square.payments(
          applicationId,
          effectiveLocationId
        );
        paymentsRef.current = payments;

        // Initialize Gift Card (styling handled via CSS)
        const giftCard = await payments.giftCard();

        // Wait for container to be in DOM
        const waitForContainer = () => {
          return new Promise((resolve, reject) => {
            let attempts = 0;
            const maxAttempts = 50;
            const checkInterval = setInterval(() => {
              const container = document.getElementById(
                containerIdRef.current
              );
              if (container) {
                clearInterval(checkInterval);
                resolve(container);
              } else if (attempts >= maxAttempts) {
                clearInterval(checkInterval);
                reject(
                  new Error(
                    'Gift card container not found in DOM after 5 seconds'
                  )
                );
              }
              attempts++;
            }, 100);
          });
        };

        await waitForContainer();
        await giftCard.attach(`#${containerIdRef.current}`);

        giftCardRef.current = giftCard;
        setReady(true);
        setInitError(null);
      } catch (err) {

        // More descriptive error messages
        let errorMessage = 'Failed to initialize Gift Card';
        if (err.message) {
          errorMessage = err.message;
        }

        // Check for specific error types
        if (err.message && err.message.includes('not found')) {
          errorMessage =
            'Unable to load Gift Card form. Please refresh the page and try again.';
        } else if (err.message && err.message.includes('not available')) {
          errorMessage =
            '⚠️ Gift Cards are not fully supported in Square Sandbox mode. Please use production credentials or test with Card payments instead.';
        } else if (err.message && err.message.includes('merchant')) {
          errorMessage = 'Gift Cards are not enabled for this merchant account';
        } else if (err.message && err.message.includes('location')) {
          errorMessage =
            'Gift Cards are not configured for this store location';
        } else if (err.message && err.message.includes('sandbox')) {
          errorMessage =
            '⚠️ Gift Cards are not fully supported in Square Sandbox mode. Please use production credentials or test with Card payments instead.';
        }

        if (!mounted) return;

        isInitializedRef.current = false; // Reset on error
        globalInitializationInProgress = false;
        setInitError(errorMessage);
        onError?.(err);
      }
    };

    loadScript();

    return () => {
      mounted = false;

      if (giftCardRef.current) {
        try {
          giftCardRef.current.destroy();
        } catch (e) {
          // Error handled silently
        }
      }

      // Reset refs and global flag on cleanup
      giftCardRef.current = null;
      isInitializedRef.current = false;
      globalInitializationInProgress = false;
    };
    // Note: We intentionally omit deps to run initialization only once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only run once

  useImperativeHandle(
    ref,
    () => ({
      tokenize: async () => {
        if (!giftCardRef.current) {
          throw new Error('Gift Card not initialized');
        }

        setIsProcessing(true);

        try {
          const result = await giftCardRef.current.tokenize();
          setIsProcessing(false);

          if (result.status === 'OK') {
            onToken?.(result.token);
            return result.token;
          }

          const message =
            (result.errors && result.errors[0] && result.errors[0].message) ||
            'Gift Card tokenization failed';

          // Enhance error message for sandbox issues
          let enhancedMessage = message;
          if (message.includes('sandbox') || message.includes('test numbers')) {
            enhancedMessage =
              '⚠️ Gift Cards are not supported in Square Sandbox mode. Please either:\n' +
              '1. Use production Square credentials, or\n' +
              '2. Test with Credit Card payment instead';
          }

          const err = new Error(enhancedMessage);
          onError?.(err);
          throw err;
        } catch (error) {
          setIsProcessing(false);

          // Enhance sandbox error messages
          if (
            error.message &&
            (error.message.includes('sandbox') ||
              error.message.includes('test numbers'))
          ) {
            const sandboxError = new Error(
              '⚠️ Gift Cards are not supported in Square Sandbox mode. Please either:\n' +
                '1. Use production Square credentials, or\n' +
                '2. Test with Credit Card payment instead'
            );
            onError?.(sandboxError);
            throw sandboxError;
          }

          onError?.(error);
          throw error;
        }
      },
      isReady: () => ready,
      clear: () => {
        if (giftCardRef.current) {
          return giftCardRef.current.clear();
        }
      },
    }),
    [ready, onToken, onError]
  );

  if (!applicationId || !effectiveLocationId) {
    return (
      <Box sx={{ padding: '20px', textAlign: 'center' }}>
        <Typography color="error" sx={{ fontFamily: 'Jost' }}>
          Gift Card configuration is missing. Please check your environment
          settings or provide a currentStoreId.
        </Typography>
      </Box>
    );
  }

  if (initError) {
    return (
      <Box sx={{ padding: '20px', textAlign: 'center' }}>
        <Typography
          color="error"
          sx={{ fontFamily: 'Jost', fontSize: '14px', whiteSpace: 'pre-line' }}
        >
          {initError}
        </Typography>
        <Typography
          sx={{
            fontFamily: 'Jost',
            fontSize: '12px',
            color: '#666',
            mt: 2,
          }}
        >
          Note: Square Gift Cards require production credentials to function
          properly.
        </Typography>
      </Box>
    );
  }

  // Check if we're in sandbox mode
  const isSandbox =
    applicationId?.includes('sandbox') || SCRIPT_URL?.includes('sandbox');

  return (
    <Box
      sx={{
        position: 'relative',
        height: !ready || isProcessing ? '140px' : 'auto',
      }}
    >
      {/* Sandbox warning */}
      {isSandbox && ready && (
        <Box
          sx={{
            mb: 2,
            p: 2,
            backgroundColor: '#FFF3CD',
            borderRadius: '8px',
            border: '1px solid #FFE69C',
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Jost',
              fontSize: '13px',
              color: '#856404',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <span role="img" aria-label="warning">
              ⚠️
            </span>{' '}
            <strong>Sandbox Mode:</strong> Gift Cards may not work in test mode.
            Use production credentials or test with Credit Card instead.
          </Typography>
        </Box>
      )}

      {/* Gift Card form will be mounted here - always render container */}
      <div
        id={containerIdRef.current}
        style={{
          // minHeight: '140px',
          width: '100%',
          opacity: ready ? 1 : 0,
          transition: 'opacity 0.3s ease',
          position: ready ? 'relative' : 'absolute',
          zIndex: ready ? 1 : 0,
        }}
      />

      {/* Loading overlay */}
      {!ready && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '40px 20px',
            // gap: 2,
            backgroundColor: 'white',
            zIndex: 2,
          }}
        >
          <CircularProgress size={24} sx={{ color: environment.mainColor }} />
          <Typography
            sx={{
              fontFamily: 'Jost',
              fontSize: '14px',
              color: '#666',
            }}
          >
            Loading Gift Card Form...
          </Typography>
        </Box>
      )}

      {/* Processing Overlay */}
      {isProcessing && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 10000,
          }}
        >
          <Box
            sx={{
              backgroundColor: '#fff',
              padding: '32px',
              borderRadius: '16px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              minWidth: '300px',
            }}
          >
            <CircularProgress
              size={48}
              sx={{ color: environment.mainColor, marginBottom: '16px' }}
            />
            <Typography
              sx={{
                fontFamily: 'Jost',
                fontSize: '18px',
                fontWeight: 600,
                color: '#1A2341',
                textAlign: 'center',
                marginBottom: '8px',
              }}
            >
              Processing Gift Card...
            </Typography>
            <Typography
              sx={{
                fontFamily: 'Jost',
                fontSize: '14px',
                color: '#7A869A',
                textAlign: 'center',
              }}
            >
              Please wait while we verify your gift card
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
});

// Custom comparison function - only re-render if these specific props change
const arePropsEqual = (prevProps, nextProps) => {
  return (
    prevProps.applicationId === nextProps.applicationId &&
    prevProps.currentStoreId === nextProps.currentStoreId &&
    prevProps.locationId === nextProps.locationId
    // Intentionally not comparing onToken/onError to prevent re-renders from function reference changes
  );
};

export default memo(SquareGiftCard, arePropsEqual);
